<template>
  <div class="indoor-level-selector">
    <b-form-select
      v-model="modelValue"
      v-bind:options="options"
      v-bind:size="size"
      v-bind:state="state"
      v-bind:disabled="disabled"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/** すべての屋内レベルID */
const ALL_INDOOR_LEVEL_ID = -1

/**
 * 表示用建物名取得
 * @param {Array.<Object>} buildings 建物
 * @returns {String} 表示用建物
 */
const getDisplayBuildingName = (buildings) => {
  return buildings.map((item) => item.short_name || item.name).join(',')
}

/**
 * 屋内レベル選択コンポーネント
 */
export default {
  name: 'AppIndoorLevelSelector',
  props: {
    /**
     * 全てのを選択している時に表示されるラベル(必須プロパティ)
     */
    selectedAllLabel: {
      type: String,
      required: true,
    },
    /**
     * 選択している屋内レベルID
     */
    value: {
      type: Number,
      default: ALL_INDOOR_LEVEL_ID,
    },
    /**
     * 選択している建物ID
     */
    indoorBuildingIds: {
      type: Array,
      default: null,
    },
    /**
     * サイズ
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: 'sm',
    },
    /**
     * 建物名を表示する場合はtrue
     */
    withBuildingName: {
      type: Boolean,
      default: true,
    },
    /**
     * バリデーション状態
     */
    state: {
      type: Boolean,
      default: null,
    },
    /**
     * 非活性の場合はtrue
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      modelValue: this.value,
    }
  },
  computed: {
    ...mapGetters('indoorLevel', {
      /**
       * 屋内レベルデータ
       * @type {Array.<Object>}
       */
      indoorLevels: 'indoorLevels',
    }),
    /**
     * 現場に紐づく屋内レベル
     * @type {Array.<Object>}
     */
    indoorLevelsFiltered: function () {
      if (!this.indoorBuildingIds) {
        return this.indoorLevels
      }
      return this.indoorLevels.filter((item) => {
        for (const building of item.indoor_buildings) {
          if (this.indoorBuildingIds.includes(building.id)) return true
        }
        return false
      })
    },
    /**
     * 選択肢
     * @type {Array.<Object>}
     */
    options: function () {
      return [
        { value: ALL_INDOOR_LEVEL_ID, text: this.selectedAllLabel },
        ...this.indoorLevelsFiltered.map((item) => ({
          value: item.id,
          text: this.withBuildingName
            ? `${item.name}(${getDisplayBuildingName(item.indoor_buildings)})`
            : item.name,
        })),
      ]
    },
    /**
     * 公開値
     * @type {Number}
     */
    publicValue: function () {
      return this.modelValue === ALL_INDOOR_LEVEL_ID ? null : this.modelValue
    },
  },
  created: function () {
    this.$_getIndoorLevels()
  },
  watch: {
    value: function () {
      this.modelValue = this.value
    },
    publicValue: function () {
      this.$_emitChangedEvent()
    },
    indoorLevelsFiltered: function () {
      // 屋内レベル未取得時は何もしない
      if (this.indoorLevels.length === 0) return
      // modelValueが選択肢になければmodelValueを初期値にする
      const isValueInOptions = this.options.some(
        (item) => item.value === this.modelValue
      )
      if (this.modelValue && !isValueInOptions) {
        this.$log.info('Clear selected value due to change options.')
        this.modelValue = ALL_INDOOR_LEVEL_ID
      }
    },
  },
  methods: {
    ...mapActions('indoorLevel', {
      /**
       * 屋内レベル更新
       */
      $_updateIndoorLevels: 'updateIndoorLevelsWithThrottle',
    }),

    /**
     * 屋内レベルデータ取得
     */
    $_getIndoorLevels: async function () {
      try {
        await this.$_updateIndoorLevels()
      } catch (error) {
        this.handleApiError(error)
      }
    },

    /**
     * 変更イベントを発行する
     */
    $_emitChangedEvent: function () {
      /**
       * 屋内レベル選択変更イベント
       *
       * @param {Number} id 屋内レベルID
       */
      this.$emit('indoor-level-changed', this.publicValue)
    },
  },
}
</script>
